import { useEffect, useState, useCallback } from "react";
import I18n from "../../../../../core/localization/I18n";
import { Button, ButtonTypes } from "../../../Button";
import { UiSizes } from "@flightpath/coreui/dist/enums";
import "./_match-columns.scss";

export const MatchColumnsStep: React.FC<{
  headerValues: string[];
  data: any[];
  fields: any[];
  initialMappings?: Record<string, string>;
  onMappingChange: (mappings: Record<string, string>) => void;
  onContinue: () => void;
  onBack: () => void;
}> = ({ headerValues, data, fields, initialMappings = {}, onMappingChange, onContinue, onBack }) => {
  const [mappings, setMappings] = useState<Record<string, string>>(initialMappings);
  const [unmappedRequiredFields, setUnmappedRequiredFields] = useState<string[]>([]);
  const [isGridInitialized, setIsGridInitialized] = useState(false);

  useEffect(() => {
    const requiredFields = fields.filter(field => field.required).map(field => field.name);
    const unmapped = requiredFields.filter(fieldName => !mappings[fieldName]);
    setUnmappedRequiredFields(unmapped);
  }, [mappings, fields]);

  const handleMappingChange = useCallback(
    (fieldName: string, headerValue: string) => {
      setMappings(prevMappings => {
        const newMappings = { ...prevMappings, [fieldName]: headerValue };
        onMappingChange(newMappings);
        return newMappings;
      });
    },
    [onMappingChange]
  );

  useEffect(() => {
    if (Object.keys(initialMappings).length === 0 && headerValues.length > 0 && !isGridInitialized) {
      const autoMappings: Record<string, string> = {};

      fields.forEach(field => {
        const exactMatch = headerValues.find(
          header =>
            header?.toLowerCase() === field.name?.toLowerCase() || header?.toLowerCase() === field.key?.toLowerCase
        );

        if (exactMatch) {
          autoMappings[field.name] = exactMatch;
        } else {
          const partialMatch = headerValues.find(
            header =>
              header.toLowerCase().includes(field.name.toLowerCase()) ||
              field.name.toLowerCase().includes(header.toLowerCase()) ||
              (field.label && header.toLowerCase().includes(field.label.toLowerCase())) ||
              (field.label && field.label.toLowerCase().includes(header.toLowerCase()))
          );

          if (partialMatch) {
            autoMappings[field.name] = partialMatch;
          }
        }
      });

      if (Object.keys(autoMappings).length > 0) {
        setMappings(autoMappings);
        onMappingChange(autoMappings);
      }

      setIsGridInitialized(true);
    }
  }, [headerValues, fields, initialMappings, onMappingChange, isGridInitialized]);

  const canContinue = unmappedRequiredFields.length === 0;

  return (
    <div className="col h-100">
      <div className="row px-3">
        <h2>{I18n.t("phrases.matchColumnsStep")}</h2>
      </div>

      <div className="row px-3">
        {I18n.t("phrases.matchColumns", {
          defaultValue: "Match your file's columns to the required system fields."
        })}
      </div>

      {unmappedRequiredFields.length > 0 && (
        <div className="row mx-0 my-3 status-banner warning-banner d-flex flex-column">
          <strong>{I18n.t("phrases.requiredFieldsNotMapped", { count: unmappedRequiredFields.length })}</strong>
          <p className="m-0">
            {I18n.t("phrases.pleaseMapRequiredFields", {
              fields: unmappedRequiredFields
                .map(f => {
                  const field = fields.find(field => field.name === f);
                  return field?.label || f;
                })
                .join(", ")
            })}
          </p>
        </div>
      )}

      {unmappedRequiredFields.length === 0 && Object.keys(mappings).length > 0 && (
        <div className="my-3 mx-0 row status-banner success-banner">
          <strong>{I18n.t("phrases.allRequiredFieldsMapped")}</strong>
        </div>
      )}

      <div className="row mb-4">
        <div className="col">
          {fields.map((field, index) => (
            <div key={index} className={`field-mapping-item ${mappings[field.name] ? "mapped-field" : ""}`}>
              <div className="field-info">
                <div className="field-label">
                  {field.label || field.name}
                  {field.required && <span className="field-required">*</span>}
                </div>
                {field.description && <div className="field-description">{field.description}</div>}
              </div>

              <div className="select-item">
                <select
                  value={mappings[field.name] || ""}
                  onChange={e => handleMappingChange(field.name, e.target.value)}
                >
                  <option value="">{I18n.t("phrases.selectColumn")}</option>
                  {headerValues.map((header, idx) => (
                    <option key={idx} value={header}>
                      {header}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="button-container row d-flex justify-content-between my-4 px-3 py-3">
        <Button type={ButtonTypes.OUTLINE_SECONDARY} size={UiSizes.LG} onClick={onBack}>
          {I18n.t("phrases.back")}
        </Button>
        <Button type={ButtonTypes.PRIMARY} onClick={onContinue} isDisabled={!canContinue}>
          <span> {I18n.t("phrases.continue")}</span>
        </Button>
      </div>
    </div>
  );
};
